import cn from "clsx";
import {
    FC,
    HTMLAttributes,
    PropsWithChildren,
    ReactElement,
    ReactNode,
} from "react";
import { MobileTable } from "./internal/MobileTable";
import { TableCaption } from "./internal/TableCaption";
import { TableCell } from "./internal/TableCell";
import { TableHeader } from "./internal/TableHeader";
import { TableRow } from "./internal/TableRow";
import styles from "./Table.module.scss";

export type TableProps = PropsWithChildren<{
    /**
     * Name of the table.  Required for accessibility but can also be used as a visual title
     */
    tableTitle: ReactNode;
    /**
     * Displays the title
     * @default false
     */
    showTitle?: boolean;
    /**
     * Style class to apply to the whole table component for custom styling
     */
    className?: string;
    /**
     * The column headers of the table
     */
    header: ReactElement;
    /**
     * Forces child elements to adhere to certain default table styles
     * @default true
     */
    isStrictStyle?: boolean;
}> &
    HTMLAttributes<HTMLTableElement>;

const InnerTable: React.FC<TableProps> & TableParts = ({
    tableTitle,
    showTitle = false,
    header,
    children,
    className = "",
    isStrictStyle = true,
    ...tableProps
}) => {
    return (
        <>
            <table
                className={cn(
                    styles.table,
                    styles["table--desktop"],
                    className,
                    {
                        [styles["table--strict"]]: isStrictStyle,
                    },
                )}
                {...tableProps}
            >
                <TableCaption showTitle={showTitle}>{tableTitle}</TableCaption>
                <thead>{header}</thead>
                <tbody className={styles.body}>{children}</tbody>
            </table>
            <MobileTable
                header={header}
                title={tableTitle}
                showTitle={showTitle}
                className={className}
                isStrictStyle={isStrictStyle}
                {...tableProps}
            >
                {children}
            </MobileTable>
        </>
    );
};

InnerTable.Row = TableRow;
InnerTable.Header = TableHeader;
InnerTable.Cell = TableCell;
type TableParts = {
    Row: typeof TableRow;
    Header: typeof TableHeader;
    Cell: typeof TableCell;
};

export const Table: FC<TableProps> & TableParts = InnerTable;
